/**
 * A hook that returns whether the category (either passed in or from context) is active.
 *
 * A category is active when it is the category in the url param for the current page or an article inside that category is in the url param.
 *
 * Uses [[useCategory]] and [[useCategoryFragment]] hooks under the hood.
 *
 * Accepts an optional categoryId as the parameter.
 *
 * - If no category is found from `useCategory`, returns `false`.
 * - If a `categoryId` is provided, it checks to see if it matches with the `category` currently in `useCategory`.
 * - If a `fragment` is found from `useCategoryFragment`, it checks if the `fragment.id` matches `categoryId`.
 * - Returns `false` if no `category`, `categoryId` or `fragment` are found.
 *
 *  @module
 */
import { useCategory } from './useCategory';
/**
 * # useIsCategoryActive
 *
 * Usage example:
 * ```javascript
 * import useIsCategoryActive from '@elevio/kb-kit/lib/hooks';
 *
 * const ActiveCategoryChecker = ({ categoryId }) => {
 *   const isActive = useIsCategoryActive(categoryId);
 *   console.log(isActive); // boolean
 *
 *   return <div>{`Category is ${isActive ? 'active ✅' : 'inactive ❌'}`}</div>;
 * };
 * ```
 */
export function useIsCategoryActive(categoryId) {
    const category = useCategory();
    // const article = useArticle();
    if (!category)
        return false;
    const _categoryId = categoryId
        ? categoryId
        : // : fragment
            // ? fragment.id
            // : article
            // ? article.category.id
            undefined;
    if (_categoryId === category.id)
        return true;
    return false;
    // const contextArticleId = React.useContext(ArticleIdContext);
    // const article = useSelector(state =>
    //   contextArticleId ? state.articleFragments.byId[contextArticleId] : undefined
    // );
    // const _categoryId =
    //   // Default the the category id param
    //   categoryId
    //     ? categoryId
    //     : // otherwise see if we can get the current category object
    //     category
    //     ? category.id
    //     : // else maybe we can get a category id from the article?
    //     article
    //     ? article.categoryId
    //     : undefined;
    // const _articleId = article ? article.id : undefined;
    // const isActive = useSelector(state =>
    //   selectors.getIsCategoryActive(state, {
    //     categoryId: _categoryId,
    //     articleId: _articleId,
    //   })
    // );
    // return isActive;
}
//# sourceMappingURL=useIsCategoryActive.js.map